import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { api, apiError, eur, frDate } from "@/lib/api";

export default function Validate() {
  const { id, token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${id}/request`, { params: { token } })
      .then((r) => setB(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail)));
  }, [id, token]);

  const decide = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/bookings/${id}/decision`, {
        token, action, reason, origin_url: window.location.origin,
      });
      setDone(data.status);
      toast.success(action === "approve" ? "Demande acceptée, lien de paiement envoyé" : "Demande refusée");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (err) return <div className="py-40 text-center text-sm text-bone/72" data-testid="validate-error">{err}</div>;
  if (!b) return <div className="py-40 text-center text-xs uppercase tracking-[0.3em] text-bone/76">Chargement…</div>;

  const treated = done || (b.status !== "requested" ? b.status : null);

  return (
    <div className="mx-auto max-w-2xl px-6 py-20" data-testid="validate-page">
      <p className="eyebrow">Espace propriétaire</p>
      <h1 className="display-xl mt-6 text-4xl text-bone">Demande de réservation</h1>

      <div className="panel mt-10 p-8 text-sm">
        {[["Gîte", b.gite_name], ["Client", b.name], ["Email", b.email],
          ["Téléphone", b.phone || "—"], ["Arrivée", frDate(b.start_date)],
          ["Départ", frDate(b.end_date)], ["Nuits", b.nights],
          ["Voyageurs", `${b.guests} (dont ${b.adults} majeurs)`],
          ["Motif du séjour", b.purpose || "—"],
          ["Séjour", eur(b.subtotal)], ["Ménage", eur(b.cleaning_fee)],
          ["Taxe de séjour", eur(b.tourist_tax)]].map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-white/10 py-3">
            <span className="text-bone/70">{k}</span><span className="text-bone">{v}</span>
          </div>
        ))}
        <div className="flex items-baseline justify-between pt-5">
          <span className="text-bone/72">Total à régler</span>
          <span className="font-display text-3xl text-bone" data-testid="validate-total">{eur(b.total)}</span>
        </div>
        {b.message && <p className="mt-6 bg-white/5 p-4 text-bone/72">« {b.message} »</p>}
      </div>

      {treated ? (
        <p className="mt-10 border border-white/10 bg-panel p-8 text-center text-sm text-bone/72" data-testid="validate-done">
          {treated === "approved" && "Demande acceptée : le client a reçu son lien de paiement sécurisé."}
          {treated === "declined" && "Demande refusée : le client a été prévenu par email."}
          {treated === "confirmed" && "Séjour déjà confirmé et payé."}
        </p>
      ) : (
        <>
          <div className="mt-10 flex flex-wrap gap-5">
            <button onClick={() => decide("approve")} disabled={!!busy} className="btn-primary" data-testid="validate-approve">
              {busy === "approve" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Accepter
            </button>
            <button onClick={() => decide("decline")} disabled={!!busy} className="btn-ghost" data-testid="validate-decline">
              {busy === "decline" ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />} Refuser
            </button>
          </div>
          <input className="field mt-6" data-testid="validate-reason" placeholder="Motif du refus (envoyé au client)"
            value={reason} onChange={(e) => setReason(e.target.value)} />
        </>
      )}
    </div>
  );
}
